import React, { useContext } from 'react';
import Immutable from 'immutable';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { Dropdown, DropdownMenuItem } from '@bibliocommons/base-dropdown';
import { catalogBibShape } from '@bibliocommons/bc-prop-types';
import Context from '@bibliocommons/context';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import RedirectActions from 'app/actions/RedirectActions';
import FullScreenOverlayActions from 'app/actions/FullScreenOverlayActions';
import { BibBriefContainer } from 'app/components/bibs/shared/BibBrief';
import AddToListOverlay from 'app/components/shared/AddToListOverlay';
import { FullScreenOverlayContainer } from 'app/components/shared/FullScreenOverlay';
import { UI_COMPONENT_TYPE } from 'app/constants/AnalyticsGa4Constants';
import { convertMetadataIdToLegacyId } from 'app/helpers/bib/bibHelpers';

export default function AddToListDropdown({
  catalogBib,
  block,
  overlayKey,
  trackAddToListClick,
  trackAddToNewListClick
}) {
  const dispatch = useDispatch();
  const { mobileApp } = useContext(Context);
  const intl = useIntl();

  const addToNewListLabel = intl.formatMessage({ id: 'add_to_list_new' });

  const bibTitle = catalogBib.getIn(['brief', 'title']);
  const metadataId = catalogBib.get('metadataId');
  const legacyId = convertMetadataIdToLegacyId(metadataId);

  function handleAddToNewList() {
    const uiEntity = {
      ui_component_label: addToNewListLabel,
      ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT
    };

    trackAddToNewListClick({ uiEntity });
    dispatch(RedirectActions.redirect(`/user_lists/new?bib=${legacyId}`, { authRequired: true }));
  }

  function openOverlay() {
    dispatch(FullScreenOverlayActions.openOverlay(overlayKey, { authRequired: true }));
  }

  function closeOverlay() {
    dispatch(FullScreenOverlayActions.closeOverlay(overlayKey));
  }

  function renderBibBrief() {
    return <BibBriefContainer metadataId={metadataId} />;
  }

  function renderNewListMenuItem() {
    return (
      <DropdownMenuItem
        handleClick={handleAddToNewList}
        item={Immutable.Map({
          label: addToNewListLabel,
          value: 'add-to-new-list'
        })}
      />
    );
  }

  function renderExistingListMenuItem() {
    const label = (
      <>
        <span aria-hidden>
          <FormattedMessage id="add_to_list_existing" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="sr_add_to_list_existing" />
        </ScreenReaderMessage>
      </>
    );
    return (
      <DropdownMenuItem
        handleClick={openOverlay}
        item={Immutable.Map({
          label,
          value: 'add-to-existing-list'
        })}
      />
    );
  }

  return mobileApp ? null : (
    <div className="cp-add-to-list-dropdown">
      <Dropdown block={block} dataKey="add-to-list-dropdown" renderLabel={() => <FormattedMessage id="add_to_list" />}>
        {renderNewListMenuItem()}
        {renderExistingListMenuItem()}
      </Dropdown>
      <FullScreenOverlayContainer dataKey="add-to-list-overlay" overlayKey={overlayKey} handleClose={closeOverlay}>
        <AddToListOverlay
          bibTitle={bibTitle}
          metadataId={metadataId}
          renderBibBrief={renderBibBrief}
          trackAddToListClick={trackAddToListClick}
        />
      </FullScreenOverlayContainer>
    </div>
  );
}

AddToListDropdown.propTypes = {
  catalogBib: catalogBibShape.isRequired,
  block: PropTypes.bool,
  overlayKey: PropTypes.string.isRequired,
  trackAddToListClick: PropTypes.func.isRequired,
  trackAddToNewListClick: PropTypes.func.isRequired
};
